import StorageService from "shared/services/StorageService";

interface CachedData<T> {
  expireAfter: number;
  value: T;
}

export default function useCachedResults<T>(cacheTTL: number) {
  async function getCachedResponse(cacheKey: string): Promise<T | null> {
    let cachedData: CachedData<T> | null = null;

    try {
      const storedValue = await StorageService.get(cacheKey);

      if (storedValue !== null) {
        cachedData = JSON.parse(storedValue);

        if (cachedData && cachedData.expireAfter < Date.now()) {
          cachedData = null;
        }
      }
    } catch {
      cachedData = null;
    }

    return cachedData?.value ?? null;
  }

  function cacheResponseData(cacheKey: string, responseData: T): void {
    StorageService.setEx(cacheKey, responseData, cacheTTL);
    StorageService.debouncedPruneKeys();
  }

  async function makeCacheKey(params: unknown): Promise<string> {
    const msgUint8 = new TextEncoder().encode(JSON.stringify(params));
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((num) => num.toString(16).padStart(2, "0")).join("");
  }

  async function withCachedResults(
    cacheKey: string,
    method: () => T,
    skipCache: boolean = false
  ): Promise<T> {
    const cachedResponse = await getCachedResponse(cacheKey);

    if (cachedResponse !== null && cachedResponse !== undefined && !skipCache)
      return cachedResponse;

    const response = await method();

    cacheResponseData(cacheKey, response);

    return response;
  }

  return {
    makeCacheKey,
    withCachedResults,
  };
}
